<template>
  <div class="error-log">
    <el-form :inline="true" class="filter-form" @submit.native.prevent="search">
      <el-form-item label="來源">
        <el-select v-model="filters.source" :disabled="loading" placeholder="請選擇來源">
          <el-option label="3gtapp" value="3gtapp" />
          <el-option label="tc" value="tc" />
          <el-option label="guideo" value="guideo" />
        </el-select>
      </el-form-item>
      <el-form-item label="錯誤代碼">
        <el-input
          v-model.trim="filters.errorCode"
          clearable
          :disabled="loading"
          placeholder="輸入錯誤代碼"
          style="width: 180px"
        />
      </el-form-item>
      <el-form-item label="應用程式">
        <el-input
          v-model.trim="filters.applicationName"
          clearable
          :disabled="loading"
          placeholder="輸入應用程式名稱"
          style="width: 220px"
        />
      </el-form-item>
      <el-form-item label="例外訊息">
        <el-input
          v-model.trim="filters.exceptionMessage"
          clearable
          :disabled="loading"
          placeholder="輸入例外訊息"
          style="width: 220px"
        />
      </el-form-item>
      <el-form-item label="紀錄時間">
        <el-date-picker
          v-model="filters.recordedAtRange"
          type="datetimerange"
          :disabled="loading"
          range-separator="至"
          start-placeholder="開始時間"
          end-placeholder="結束時間"
          format="yyyy-MM-dd HH:mm:ss"
          value-format="yyyy-MM-dd HH:mm:ss"
          clearable
        />
      </el-form-item>
      <el-form-item label="排序">
        <el-select v-model="filters.recordedAtOrder" :disabled="loading" style="width: 100px">
          <el-option label="新到舊" value="desc" />
          <el-option label="舊到新" value="asc" />
        </el-select>
      </el-form-item>
      <el-form-item label="每次載入">
        <el-select v-model="limit" :disabled="loading" style="width: 90px" @change="search">
          <el-option :value="10" label="10 筆" />
          <el-option :value="20" label="20 筆" />
          <el-option :value="50" label="50 筆" />
        </el-select>
      </el-form-item>
      <div style="margin: 8px 0 0 8px">
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search" circle>
        </el-button>
        <el-button icon="el-icon-refresh" :disabled="loading" @click="resetFilters" circle>
        </el-button>
      </div>
    </el-form>

    <div ref="listContainer" class="list-container" @scroll.passive="handleScroll">
      <el-table
        v-loading="loading && events.length === 0"
        :data="events"
        border
        fit
        highlight-current-row
        size="mini"
        style="width: 100%"
      >
        <el-table-column align="center" label="項次" width="60">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="紀錄時間" width="170">
          <template slot-scope="scope">
            {{ scope.row.recorded_at || '-' }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="Event ID" min-width="250" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.error_event_id || '-' }}
          </template>
        </el-table-column>
        <el-table-column align="left" label="來源 / 應用程式" min-width="200">
          <template slot-scope="scope">
            <div>{{ scope.row.source || '-' }}</div>
            <el-tag size="mini" type="info">{{ scope.row.application_name || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" label="HTTP 狀態" width="105">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status_code)">
              {{ scope.row.status_code || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column align="left" label="錯誤標題 / 代碼" min-width="200">
          <template slot-scope="scope">
            <div>{{ scope.row.title || '-' }}</div>
            <el-tag v-if="scope.row.error_code" size="mini" type="warning">
              {{ scope.row.error_code }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column align="left" label="例外訊息" min-width="300" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.exception_message || '-' }}
          </template>
        </el-table-column>
        <el-table-column align="center" label="詳細內容" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showDetails(scope.row)"> 查看 </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="load-more">
        <span v-if="events.length">{{ `已載入 ${events.length} 筆` }}</span>
        <span v-if="!hasMore && events.length">已無更多資料</span>
        <span v-else-if="!loading && !events.length">查無資料</span>
        <el-button v-if="hasMore && events.length" type="text" :loading="loading" @click="loadMore">
          載入更多
        </el-button>
      </div>
    </div>

    <el-dialog
      title="異常事件詳細資料"
      :visible.sync="detailVisible"
      width="850px"
      @closed="selectedEvent = null"
    >
      <div v-if="selectedEvent" class="event-details">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="Event ID">
            {{ selectedEvent.error_event_id || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="紀錄時間">
            {{ selectedEvent.recorded_at || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="來源">
            {{ selectedEvent.source || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="應用程式">
            {{ selectedEvent.application_name || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="HTTP 狀態">
            {{ selectedEvent.status_code || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="錯誤代碼">
            {{ selectedEvent.error_code || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="關聯 ID" :span="2">
            {{ selectedEvent.correlation_id || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="錯誤標題" :span="2">
            {{ selectedEvent.title || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="例外類型" :span="2">
            {{ selectedEvent.exception_type || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="例外訊息" :span="2">
            {{ selectedEvent.exception_message || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="例外來源" :span="2">
            {{ selectedEvent.exception_source || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="目標方法" :span="2">
            {{ selectedEvent.target_site || '-' }}
          </el-descriptions-item>
        </el-descriptions>

        <el-collapse class="detail-collapse">
          <el-collapse-item title="Problem Details" name="problem-details">
            <pre class="detail-json">{{ formatJson(selectedEvent.problem_details) }}</pre>
          </el-collapse-item>
          <el-collapse-item
            :title="`Stack Frames (${stackFrames(selectedEvent).length})`"
            name="stack-frames"
          >
            <el-table
              v-if="stackFrames(selectedEvent).length"
              :data="stackFrames(selectedEvent)"
              border
              size="mini"
              max-height="360"
            >
              <el-table-column prop="frame_index" label="#" width="55" />
              <el-table-column prop="method_name" label="方法" min-width="240" />
              <el-table-column prop="file_path" label="檔案" min-width="240" />
              <el-table-column prop="line_number" label="行號" width="70" />
              <el-table-column label="堆疊文字" min-width="300">
                <template slot-scope="scope">
                  <pre class="frame-text">{{ scope.row.frame_text || '-' }}</pre>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="沒有 Stack Frames" />
          </el-collapse-item>
        </el-collapse>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getErrorEvents } from '@/api/event'

export default {
  name: 'ErrorLog',
  data() {
    return {
      filters: {
        source: '3gtapp',
        errorCode: '',
        applicationName: '',
        exceptionMessage: '',
        recordedAtRange: [],
        recordedAtOrder: 'desc',
      },
      limit: 20,
      offset: 0,
      events: [],
      loading: false,
      hasMore: true,
      detailVisible: false,
      selectedEvent: null,
    }
  },
  created() {
    this.fetchEvents()
  },
  methods: {
    search() {
      if (this.loading) return

      this.events = []
      this.offset = 0
      this.hasMore = true
      if (this.$refs.listContainer) this.$refs.listContainer.scrollTop = 0
      this.fetchEvents()
    },
    resetFilters() {
      this.filters = {
        source: '3gtapp',
        errorCode: '',
        applicationName: '',
        exceptionMessage: '',
        recordedAtRange: [],
        recordedAtOrder: 'desc',
      }
      this.limit = 20
      this.search()
    },
    async fetchEvents() {
      if (this.loading || !this.hasMore) return

      this.loading = true
      const params = {
        source: this.filters.source,
        recorded_at_order: this.filters.recordedAtOrder,
        limit: this.limit,
        offset: this.offset,
      }
      if (this.filters.errorCode) params.error_code = this.filters.errorCode
      if (this.filters.applicationName) params.application_name = this.filters.applicationName
      if (this.filters.exceptionMessage) params.exception_message = this.filters.exceptionMessage
      if (this.filters.recordedAtRange && this.filters.recordedAtRange.length === 2) {
        params.recorded_at_from = this.filters.recordedAtRange[0].replace(' ', 'T')
        params.recorded_at_to = this.filters.recordedAtRange[1].replace(' ', 'T')
      }

      try {
        const response = await getErrorEvents(params)
        if (!response || !response.success || !Array.isArray(response.data)) {
          this.$message.error((response && response.message) || '查詢異常事件失敗')
          return
        }

        this.events = this.events.concat(response.data)
        this.offset += response.data.length
        this.hasMore = response.data.length === this.limit
      } catch (error) {
        this.$message.error(error.message || '查詢異常事件失敗')
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.fetchEvents()
    },
    handleScroll() {
      const container = this.$refs.listContainer
      if (
        container &&
        container.scrollTop + container.clientHeight >= container.scrollHeight - 80
      ) {
        this.loadMore()
      }
    },
    showDetails(event) {
      this.selectedEvent = event
      this.detailVisible = true
    },
    statusTagType(statusCode) {
      if (statusCode >= 500) return 'danger'
      if (statusCode >= 400) return 'warning'
      return 'info'
    },
    stackFrames(event) {
      return event && Array.isArray(event.stack_frames) ? event.stack_frames : []
    },
    formatJson(value) {
      return JSON.stringify(value || {}, null, 2)
    },
  },
}
</script>

<style lang="scss" scoped>
.error-log {
  padding: 15px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.list-container {
  height: calc(100vh - 230px);
  min-height: 300px;
  overflow: auto;
}

.load-more {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  color: #909399;
}

.event-details {
  max-height: 65vh;
  overflow: auto;
}

.detail-collapse {
  margin-top: 16px;
}

.detail-json,
.frame-text {
  overflow: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-json {
  max-height: 360px;
  padding: 12px;
  background: #f5f7fa;
}
</style>
